/**
 * KontrolyModal — the checks of one fleet car (per-row modal): oil, brakes, tyres and rental-specific checks with a
 * date limit and / or a km limit, due when the date passes or the km is reached, whichever comes first. The API
 * judges them (computed on read from the odometer series); this only shows the verdict. `pujcovna:write` adds,
 * edits, completes, reopens and removes; `read` sees the same list. STK / servis stay the stock car's own dates.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Badge } from '@/components/Badge';
import type { BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatDate, formatInt } from '@/lib/format';
import { pragueToday } from '@/lib/prague-date';
import {
  useAddKontrolaMutation,
  useCompleteKontrolaMutation,
  useKontrolyQuery,
  useRemoveKontrolaMutation,
  useReopenKontrolaMutation,
  useSetServisIntervalMutation,
  useUpdateKontrolaMutation,
  type Kontrola,
  type KontrolaStav,
  type ServisPrehled,
} from './api';
import { dnu, remaining } from './kontroly-helpers';

const STAV: Record<KontrolaStav, { label: string; tone: BadgeTone }> = {
  po_terminu: { label: 'Po termínu', tone: 'danger' },
  blizi_se: { label: 'Blíží se', tone: 'warn' },
  v_poradku: { label: 'V pořádku', tone: 'ok' },
  splnena: { label: 'Splněno', tone: 'neutral' },
};

const digits = (s: string) => s.replace(/\D/g, '');

/** How many days away a stock date is, in words ("za 40 dní", "po termínu o 3 dny"). */
function datumHint(zbyva: number | null): string {
  if (zbyva == null) return '';
  return zbyva < 0 ? `po termínu o ${dnu(-zbyva)}` : zbyva === 0 ? 'dnes' : `za ${dnu(zbyva)}`;
}

/**
 * STK and servisní prohlídka of the car. The DATES are the stock car's (one source: they move when a servis úkon
 * is confirmed as done) and are only shown here; the km part is the fleet entry's and is edited here. The same
 * confirmation that moves the date re-arms the km, so the two cannot be armed apart.
 */
function ServisPanel({
  servis,
  canWrite,
  busy,
  onSave,
}: {
  servis: ServisPrehled;
  canWrite: boolean;
  busy: boolean;
  onSave: (v: { interval_km: number | null; posledni_km: number | null }) => void;
}) {
  const [interval, setInterval] = useState(servis.interval_km != null ? String(servis.interval_km) : '');
  const [posledni, setPosledni] = useState(servis.posledni_km != null ? String(servis.posledni_km) : '');
  const km = servis.stav ? STAV[servis.stav] : null;
  const intervalNum = interval ? Number(interval) : null;
  const invalid = intervalNum != null && (intervalNum < 1000 || intervalNum > 200000);
  const dirty =
    (servis.interval_km ?? null) !== intervalNum || (servis.posledni_km ?? null) !== (intervalNum == null || !posledni ? null : Number(posledni));

  return (
    <section data-testid="kontroly-servis" className="flex flex-col gap-3 border border-line p-4">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">STK a servisní prohlídka</div>
      <dl className="grid gap-x-6 gap-y-2 text-[13px] sm:grid-cols-[auto_1fr]">
        <dt className="text-ink-3">STK</dt>
        <dd data-testid="kontroly-stk" className="text-ink">
          {servis.stk_platnost ? (
            <>
              platí do <span className="font-mono">{formatDate(servis.stk_platnost)}</span>
              <span className="text-ink-2"> · {datumHint(servis.stk_zbyva_dni)}</span>
            </>
          ) : (
            <span className="text-ink-2">termín není ve skladu vyplněn</span>
          )}
        </dd>
        <dt className="text-ink-3">Servisní prohlídka</dt>
        <dd className="flex flex-col gap-1 text-ink">
          <span data-testid="kontroly-serviska">
            {servis.serviska ? (
              <>
                termín <span className="font-mono">{formatDate(servis.serviska)}</span>
                <span className="text-ink-2"> · {datumHint(servis.serviska_zbyva_dni)}</span>
              </>
            ) : (
              <span className="text-ink-2">termín není ve skladu vyplněn</span>
            )}
          </span>
          <span data-testid="kontroly-servis-km" className="flex flex-wrap items-center gap-x-2">
            {servis.interval_km == null ? (
              <span className="text-ink-2">podle nájezdu se nesleduje</span>
            ) : servis.dalsi_km == null ? (
              <span className="text-ink-2">
                každých {formatInt(servis.interval_km)} km — doplňte nájezd poslední prohlídky, nebo ho zapíše další potvrzená prohlídka
              </span>
            ) : (
              <>
                {km && (
                  <Badge tone={km.tone} size="sm">
                    {km.label}
                  </Badge>
                )}
                <span>
                  při <span className="font-mono">{formatInt(servis.dalsi_km)} km</span>
                  <span className="text-ink-2">
                    {' '}
                    · každých {formatInt(servis.interval_km)} km, naposledy při {formatInt(servis.posledni_km)} km
                    {servis.zbyva_km != null &&
                      ` · ${servis.zbyva_km < 0 ? `překročeno o ${formatInt(-servis.zbyva_km)} km` : `zbývá ${formatInt(servis.zbyva_km)} km`}`}
                  </span>
                </span>
              </>
            )}
          </span>
        </dd>
      </dl>
      <p className="text-[12px] text-ink-3">
        Termíny STK a servisní prohlídky se mění ve skladu — potvrzením servisního úkonu jako „Proběhlo“, které zároveň
        zapíše nájezd poslední prohlídky.
      </p>
      {canWrite && (
        <form
          data-testid="kontroly-servis-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (busy || invalid || !dirty) return;
            onSave({ interval_km: intervalNum, posledni_km: intervalNum == null || !posledni ? null : Number(posledni) });
          }}
          className="flex flex-col gap-3"
        >
          <Fields cols={2}>
            <Field label="Servisní interval" optional="volitelné" state={invalid ? 'error' : 'default'} hint={invalid ? 'Zadejte 1 000 – 200 000 km.' : undefined}>
              <TextInput
                value={interval}
                inputMode="numeric"
                suffix="km"
                maxLength={6}
                disabled={busy}
                onChange={(e) => setInterval(digits(e.currentTarget.value))}
              />
            </Field>
            <Field label="Naposledy při" optional="volitelné">
              <TextInput
                value={posledni}
                inputMode="numeric"
                suffix="km"
                maxLength={7}
                disabled={busy || !interval}
                onChange={(e) => setPosledni(digits(e.currentTarget.value))}
              />
            </Field>
          </Fields>
          <div>
            <Button variant="secondary" size="sm" type="submit" disabled={busy || invalid || !dirty}>
              Uložit interval
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

interface KontrolyModalProps {
  /** The fleet entry id. */
  id: number;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  canWrite: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY = { nazev: '', datum: '', km: '', poznamka: '' };

export function KontrolyModal({ id, title, canWrite, open, onOpenChange }: KontrolyModalProps) {
  const query = useKontrolyQuery(id);
  const add = useAddKontrolaMutation(id);
  const update = useUpdateKontrolaMutation(id);
  const complete = useCompleteKontrolaMutation(id);
  const reopen = useReopenKontrolaMutation(id);
  const remove = useRemoveKontrolaMutation(id);
  const setServis = useSetServisIntervalMutation(id);
  const { confirm, dialog } = useConfirm();
  const [form, setForm] = useState(EMPTY);
  /** The check the form edits; null = a new one. */
  const [editing, setEditing] = useState<number | null>(null);
  /** The check being completed inline, with its date and km. */
  const [completing, setCompleting] = useState<{ id: number; datum: string; km: string } | null>(null);
  const busy =
    add.isPending || update.isPending || complete.isPending || reopen.isPending || remove.isPending || setServis.isPending;

  // Every way out ends in `onOpenChange(false)`; while a write is pending it would unmount the observer its
  // toasts hang on (the same rule as the other fleet modals), so it waits.
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  // The modal mounts per open: it renders only from a read that finished after the mount, never a cached snapshot.
  const fresh = query.isSuccess && query.isFetchedAfterMount;
  const data = query.data;
  const known = (kid: number) => data?.kontroly.find((k) => k.id === kid);

  const fail = (fallback: string) => (err: unknown) => toast.error(`Chyba: ${(err as ApiError).message ?? fallback}`);
  const reset = () => {
    setForm(EMPTY);
    setEditing(null);
  };
  const set = (patch: Partial<typeof EMPTY>) => setForm((f) => ({ ...f, ...patch }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const nazev = form.nazev.trim();
    if (!nazev) {
      toast.error('Vyplňte název kontroly.');
      return;
    }
    if (!form.datum && !form.km) {
      toast.error('Zadejte termín, limit nájezdu, nebo obojí.');
      return;
    }
    const body = {
      nazev,
      limit_datum: form.datum || null,
      limit_km: form.km ? Number(form.km) : null,
      poznamka: form.poznamka.trim(),
    };
    if (editing != null) {
      update.mutate(
        { kid: editing, body },
        {
          onSuccess: () => {
            toast.ok('Kontrola uložena.');
            reset();
          },
          onError: fail('Nepodařilo se uložit kontrolu.'),
        },
      );
    } else {
      add.mutate(body, {
        onSuccess: () => {
          toast.ok('Kontrola přidána.');
          reset();
        },
        onError: fail('Nepodařilo se přidat kontrolu.'),
      });
    }
  };

  const startEdit = (k: Kontrola) => {
    setCompleting(null);
    setEditing(k.id);
    setForm({ nazev: k.nazev, datum: k.limit_datum ?? '', km: k.limit_km != null ? String(k.limit_km) : '', poznamka: k.poznamka });
  };

  const startComplete = (k: Kontrola) => {
    reset();
    setCompleting({ id: k.id, datum: pragueToday(), km: data?.aktualni_km != null ? String(data.aktualni_km) : '' });
  };

  const submitComplete = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !completing || !completing.datum) return;
    const kid = completing.id;
    complete.mutate(
      { kid, body: { datum: completing.datum, km: completing.km ? Number(completing.km) : null } },
      {
        onSuccess: () => {
          toast.ok('Kontrola splněna.');
          setCompleting(null);
        },
        onError: fail('Nepodařilo se označit kontrolu jako splněnou.'),
      },
    );
  };

  const doReopen = (k: Kontrola) =>
    reopen.mutate(k.id, {
      onSuccess: () => toast.ok('Kontrola znovu otevřena.'),
      onError: fail('Nepodařilo se znovu otevřít kontrolu.'),
    });

  const doRemove = async (k: Kontrola) => {
    const ok = await confirm({
      title: (
        <>
          Odebrat kontrolu <em>{k.nazev}</em>?
        </>
      ),
      description: 'Kontrola zmizí ze seznamu vozidla, v historii změn zůstane.',
      confirmLabel: 'Odebrat',
    });
    if (!ok) return;
    remove.mutate(k.id, {
      onSuccess: () => {
        toast.ok('Kontrola odebrána.');
        if (editing === k.id) reset();
        if (completing?.id === k.id) setCompleting(null);
      },
      onError: fail('Nepodařilo se odebrat kontrolu.'),
    });
  };

  return (
    <>
    <Modal open={open} onOpenChange={guarded} maxWidth={760}>
      <DialogCrown
        eyebrow="Vozidlo půjčovny"
        title={
          <>
            Kontroly <em>{title}</em>
          </>
        }
        sub="Kontrola je splatná, když uplyne termín, nebo vozidlo dojede limit nájezdu — podle toho, co nastane dřív. STK a servisní prohlídka mají vlastní blok."
      />
      <Modal.Body className="px-8 py-5">
        {query.isError ? (
          <p data-testid="kontroly-error" className="text-[13px] text-ink-2">
            Kontroly se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
          </p>
        ) : !fresh || !data ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div data-testid="kontroly-najezd" className="flex flex-wrap items-baseline gap-x-3 text-[13px] text-ink-2">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Nájezd vozu</span>
              {data.aktualni_km != null ? (
                <span>
                  <span className="font-mono text-ink">{formatInt(data.aktualni_km)} km</span>
                  {data.aktualni_km_datum && <span> · k {formatDate(data.aktualni_km_datum)}</span>}
                  {data.km_za_den != null && <span> · cca {formatInt(Math.round(data.km_za_den))} km/den</span>}
                </span>
              ) : (
                <span>zatím není znám — kontroly podle nájezdu se nevyhodnotí, dokud nebude zapsán odečet</span>
              )}
            </div>

            <ServisPanel
              // re-seeded from the server's answer after a save or a confirmation elsewhere
              key={`${data.servis.interval_km}-${data.servis.posledni_km}`}
              servis={data.servis}
              canWrite={canWrite}
              busy={busy}
              onSave={(v) =>
                setServis.mutate(v, {
                  onSuccess: () => toast.ok(v.interval_km == null ? 'Sledování podle nájezdu vypnuto.' : 'Servisní interval uložen.'),
                  onError: fail('Nepodařilo se uložit servisní interval.'),
                })
              }
            />

            {canWrite && (
              <form data-testid="kontroly-form" onSubmit={submit} className="flex flex-col gap-4 border border-line p-4">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  {editing != null ? `Upravit kontrolu · ${known(editing)?.nazev ?? ''}` : 'Nová kontrola'}
                </div>
                <Fields cols={2}>
                  <Field label="Název" required>
                    <TextInput value={form.nazev} maxLength={63} disabled={busy} onChange={(e) => set({ nazev: e.currentTarget.value })} />
                  </Field>
                  <Field label="Poznámka" optional="volitelné">
                    <TextInput value={form.poznamka} maxLength={100} disabled={busy} onChange={(e) => set({ poznamka: e.currentTarget.value })} />
                  </Field>
                  <Field label="Termín" optional="volitelné">
                    <TextInput type="date" value={form.datum} disabled={busy} onChange={(e) => set({ datum: e.currentTarget.value })} />
                  </Field>
                  <Field label="Limit nájezdu" optional="volitelné">
                    <TextInput
                      value={form.km}
                      inputMode="numeric"
                      suffix="km"
                      maxLength={7}
                      disabled={busy}
                      onChange={(e) => set({ km: digits(e.currentTarget.value) })}
                    />
                  </Field>
                </Fields>
                <div className="flex flex-wrap gap-2">
                  <Button variant="ink" size="sm" type="submit" disabled={busy || !form.nazev.trim() || (!form.datum && !form.km)}>
                    {editing != null ? 'Uložit kontrolu' : 'Přidat kontrolu'}
                  </Button>
                  {editing != null && (
                    <Button variant="secondary" size="sm" type="button" onClick={reset} disabled={busy}>
                      Zrušit úpravu
                    </Button>
                  )}
                </div>
              </form>
            )}

            {data.kontroly.length === 0 ? (
              <p data-testid="kontroly-prazdno" className="text-[13px] text-ink-2">
                Pro toto vozidlo zatím není zapsána žádná kontrola.
              </p>
            ) : (
              <div data-testid="kontroly-seznam" className="flex flex-col">
                <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  Kontroly · {data.kontroly.length}
                </div>
                <ul className="border border-line">
                  {data.kontroly.map((k) => {
                    const stav = STAV[k.stav];
                    const limits = [
                      k.limit_datum ? `termín ${formatDate(k.limit_datum)}` : null,
                      k.limit_km != null ? `limit ${formatInt(k.limit_km)} km` : null,
                    ].filter(Boolean);
                    const note =
                      k.stav === 'splnena'
                        ? [
                            `splněno ${formatDate(k.splneno_datum ?? '')}`,
                            k.splneno_km != null ? `při ${formatInt(k.splneno_km)} km` : null,
                          ].filter(Boolean)
                        : remaining(k);
                    return (
                      <li
                        key={k.id}
                        data-testid={`kontrola-${k.id}`}
                        data-stav={k.stav}
                        className="flex flex-col gap-2 border-b border-line px-3 py-3 last:border-b-0"
                      >
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <Badge tone={stav.tone} size="sm">
                            {stav.label}
                          </Badge>
                          <span className="min-w-0 flex-1 truncate text-[14px] text-ink">{k.nazev}</span>
                          {canWrite && (
                            <span className="flex flex-wrap gap-2">
                              {k.stav === 'splnena' ? (
                                <Button variant="secondary" size="sm" disabled={busy} onClick={() => doReopen(k)}>
                                  Znovu otevřít
                                </Button>
                              ) : (
                                <>
                                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => startComplete(k)}>
                                    Splnit
                                  </Button>
                                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => startEdit(k)}>
                                    Upravit
                                  </Button>
                                </>
                              )}
                              <Button variant="secondary" size="sm" disabled={busy} onClick={() => doRemove(k)}>
                                Odebrat
                              </Button>
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-ink-2">
                          {limits.length > 0 && <span className="font-mono">{limits.join(' · ')}</span>}
                          {note.length > 0 && <span data-testid={`kontrola-${k.id}-zbyva`}>{note.join(' · ')}</span>}
                          {k.poznamka && <span>{k.poznamka}</span>}
                        </div>
                        {canWrite && completing?.id === k.id && (
                          <form
                            data-testid="kontroly-splnit-form"
                            onSubmit={submitComplete}
                            className="flex flex-col gap-3 border border-line p-3"
                          >
                            <Fields cols={2}>
                              <Field label="Splněno dne" required>
                                <TextInput
                                  type="date"
                                  value={completing.datum}
                                  max={pragueToday()}
                                  disabled={busy}
                                  onChange={(e) => setCompleting({ ...completing, datum: e.currentTarget.value })}
                                />
                              </Field>
                              <Field label="Při nájezdu" optional="volitelné">
                                <TextInput
                                  value={completing.km}
                                  inputMode="numeric"
                                  suffix="km"
                                  maxLength={7}
                                  disabled={busy}
                                  onChange={(e) => setCompleting({ ...completing, km: digits(e.currentTarget.value) })}
                                />
                              </Field>
                            </Fields>
                            <div className="flex flex-wrap gap-2">
                              <Button variant="ink" size="sm" type="submit" disabled={busy || !completing.datum}>
                                Označit jako splněnou
                              </Button>
                              <Button variant="secondary" size="sm" type="button" disabled={busy} onClick={() => setCompleting(null)}>
                                Zrušit
                              </Button>
                            </div>
                          </form>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Foot
        actions={
          <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
            Zavřít
          </Button>
        }
      />
    </Modal>
    {dialog}
    </>
  );
}
